import React, { useState } from 'react';
import theme from './OrderLineTab.module.scss';
import { Order, OrderLine } from '@/types';
import { CardSection, SimpleCard } from '@/components/Cards';
import { Trans } from '@lingui/react/macro';
import ProfileAndUserDisplay from '@/components/ProfileAndUserDisplay';
import { Tooltip } from '@/components/Tooltip';
import { Button, ConfirmButton } from '@/components/Buttons';
import Banner from '@/components/Banner';
import { faEdit, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { PriceDisplay } from '@/seller/components/fees';
import { ProjectCurrencyDisplay } from '@/seller/components/currency';
import { sum } from 'lodash-es';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import OrderLineNoteModal from './OrderLineNoteModal';
import { deleteOrderLineNote } from '@/services/orders';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import Tags from '@/components/Tags';

interface OrderLineTabProps {
    order: Order;
    onOrderChange(): void;
}

const OrderLineTab: React.FC<OrderLineTabProps> = ({ order, onOrderChange }) => {
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedOrderLine, setSelectOrderLine] = useState<OrderLine | null>(null);

    const canSeeAllPrices = order.orderLines.every((x) => !!x.priceCalculation);

    const deleteNote = async (orderLineId: number) => {
        await deleteOrderLineNote(orderLineId);
        triggerHandler(onOrderChange);
    };

    const renderLines = () => {
        // if (order.buyerOfferId === null && order.buyerOptionGroupId === null && order.buyerOptionPresetId === null) {
        //     return null;
        // }

        return order.orderLines.map((x) => (
            <SimpleCard key={x.id} className={theme.orderLineCard} variant="flat">
                <CardSection
                    caption={<Trans id="OrderLineItem">Item</Trans>}
                    contentClassName={theme.orderLineDescription}
                    grow>
                    {x.itemName}
                    <small className="text-faded">
                        {' '}
                        <i>{x.customFieldData}</i>
                    </small>
                </CardSection>
                {order.buyerOptionGroupId !== null && x.productInfo && (
                    <CardSection caption={<Trans>Product ID</Trans>} grow shrink width="100px">
                        {x.productInfo.sku}
                    </CardSection>
                )}
                <CardSection caption={<Trans>Category</Trans>} width="100px" shrink grow>
                    <ItemCategoryDisplay value={x.itemCategoryId} />
                </CardSection>
                {order.buyerOptionGroupId !== null && (
                    <CardSection caption={<Trans>Qty</Trans>} width="80px" shrink grow>
                        {x.quantity}
                    </CardSection>
                )}
                <CardSection caption={<Trans>Fee preset</Trans>} width="100px" shrink grow>
                    {x.projectFeeSetName}
                </CardSection>
                <CardSection contentClassName={theme.noteContent} caption={<Trans>Note</Trans>} width="100px" shrink grow>
                    {x.note ? (
                        <>
                            <Tooltip
                                color="light"
                                title={
                                    <Trans>
                                        <strong>Note</strong>
                                    </Trans>
                                }
                                className={theme.noteTooltip}
                                content={
                                    <div className="preserve-white-space">
                                        <div className={theme.toolTipText}>{x.note}</div>
                                        <ProfileAndUserDisplay
                                            mode="user"
                                            userId={x.noteLastUpdatedByUserId ?? undefined}
                                            userName={x.noteLastUpdatedByUserDisplayName}
                                            prependContent={
                                                <small>
                                                    <Trans>Last updated by</Trans>
                                                </small>
                                            }
                                        />
                                    </div>
                                }>
                                <div className={theme.noteText}>{x.note}</div>
                            </Tooltip>
                            <Button
                                icon={faEdit}
                                color="text"
                                size="small"
                                onClick={() => {
                                    setIsNoteModalOpen(true);
                                    setSelectOrderLine(x);
                                }}
                            />
                            <ConfirmButton
                                dialogTitle={<Trans>Delete note</Trans>}
                                dialogBody={
                                    <>
                                        <Trans>
                                            You are about to delete the note connected to this order line, are you sure you
                                            want to do this?
                                        </Trans>
                                        <Banner type="warning" className={theme.deleteBanner}>
                                            {
                                                <Trans>
                                                    <strong>Please note,</strong>
                                                    if deleted, this note will not appear on the room form export
                                                </Trans>
                                            }
                                        </Banner>
                                    </>
                                }
                                confirmButtonText={<Trans>Delete</Trans>}
                                dialogConfirmButtonColor="danger"
                                icon={faTrashAlt}
                                color="text"
                                onConfirm={() => {
                                    deleteNote(x.id);
                                }}
                            />
                        </>
                    ) : (
                        <Button
                            color="link"
                            size="small"
                            className={theme.addNote}
                            onClick={() => {
                                setIsNoteModalOpen(true);
                                setSelectOrderLine(x);
                            }}>
                            {<Trans>Add note</Trans>}
                        </Button>
                    )}
                </CardSection>
                <CardSection shrink grow width="150px" caption={<Trans>Tags</Trans>}>
                    <Tags limit={2} size="small" tags={x.tags} />
                </CardSection>
                <CardSection caption={<Trans>Price</Trans>} width="100px" shrink grow className="text-right">
                    <PriceDisplay value={x.priceCalculation} redacted={x.priceCalculation === null} />
                </CardSection>
            </SimpleCard>
        ));
    };

    return (
        <>
            {renderLines()}

            {selectedOrderLine && (
                <OrderLineNoteModal
                    isOpen={isNoteModalOpen}
                    orderLine={selectedOrderLine}
                    onClose={() => setIsNoteModalOpen(false)}
                    onSubmit={onOrderChange}
                />
            )}

            {canSeeAllPrices && (
                <div className={theme.orderSumCard}>
                    <div className={theme.total}>
                        <Trans>Total price</Trans>

                        <div className={theme.right}>
                            <ProjectCurrencyDisplay
                                value={sum(order.orderLines.map((x) => x.priceCalculation!.finalPrice))}
                            />
                        </div>
                    </div>
                    <div className={theme.subtotal}>
                        <div className={theme.right}>
                            (
                            <ProjectCurrencyDisplay
                                value={sum(order.orderLines.map((x) => x.priceCalculation!.basePrice))}
                            />
                            )
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OrderLineTab;
